<template>
  <HrPageLayout
    title="Incoming Documents"
    subtitle="County Attorney · Correspondence register (active queue = within 7 days of correspondence date; archived remains searchable)"
    icon="📥"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'County Attorney' },
      { label: 'Incoming Documents' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreate">
        <i class="bx bx-plus"></i> Log incoming
      </button>
      <button type="button" class="hr-btn hr-btn-outline-primary" @click="loadRows" :disabled="loading">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="iad-toolbar hr-mb-3">
      <div class="tabs">
        <button
          type="button"
          :class="['tab', queue === 'active' ? 'on' : '']"
          @click="setQueue('active')"
        >
          Active queue
        </button>
        <button
          type="button"
          :class="['tab', queue === 'archived' ? 'on' : '']"
          @click="setQueue('archived')"
        >
          Archived
        </button>
        <button type="button" :class="['tab', queue === 'all' ? 'on' : '']" @click="setQueue('all')">All</button>
      </div>
      <div class="filters">
        <label>
          <span>Status</span>
          <select v-model="filters.status" class="hr-select" @change="loadRows">
            <option value="">Any</option>
            <option value="Received">Received</option>
            <option value="Under Review">Under Review</option>
            <option value="Responded">Responded</option>
            <option value="Filed">Filed</option>
          </select>
        </label>
        <label>
          <span>Received from</span>
          <input v-model="filters.date_from" type="date" class="hr-input" @change="loadRows" />
        </label>
        <label>
          <span>Received to</span>
          <input v-model="filters.date_to" type="date" class="hr-input" @change="loadRows" />
        </label>
        <label class="grow">
          <span>Sender contains</span>
          <input v-model="filters.sender" type="text" class="hr-input" placeholder="Sender" @keyup.enter="loadRows" />
        </label>
        <label class="grow">
          <span>Ref contains</span>
          <input v-model="filters.ref_number" type="text" class="hr-input" placeholder="Reference" @keyup.enter="loadRows" />
        </label>
        <label class="grow">
          <span>Keyword</span>
          <input v-model="filters.keyword" type="text" class="hr-input" placeholder="Subject, notes…" @keyup.enter="loadRows" />
        </label>
        <button type="button" class="hr-btn hr-btn-secondary" @click="resetFilters">Clear filters</button>
        <button type="button" class="hr-btn hr-btn-primary" @click="loadRows">Search</button>
      </div>
    </div>

    <div v-if="installHint" class="hr-alert hr-alert-warning hr-mb-3">{{ installHint }}</div>

    <div class="hr-chart-card">
      <div class="hr-chart-body">
        <div v-if="loading" class="hr-loading-wrap">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted">Loading…</p>
        </div>
        <div v-else class="hr-table-wrapper">
          <table class="hr-table hr-table-hover">
            <thead>
              <tr>
                <th>Received</th>
                <th>Correspondence</th>
                <th>Sender</th>
                <th>Subject</th>
                <th>Ref</th>
                <th>Handler</th>
                <th>Status</th>
                <th>Archived</th>
                <th>File</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r.id">
                <td>{{ r.received_date || "—" }}</td>
                <td>{{ r.correspondence_date || "—" }}</td>
                <td>{{ r.sender }}</td>
                <td>{{ r.subject }}</td>
                <td>{{ r.ref_number }}</td>
                <td>{{ r.handler }}</td>
                <td><span class="hr-badge hr-badge-info">{{ r.status }}</span></td>
                <td>{{ r.archived_at || "—" }}</td>
                <td class="iad-file-actions">
                  <template v-if="r.file_path">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="viewFile(r.id)">
                      View
                    </button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="downloadFile(r.id)">
                      Download
                    </button>
                  </template>
                  <span v-else class="hr-text-muted">—</span>
                </td>
                <td>
                  <button
                    v-if="!r.archived_at"
                    type="button"
                    class="hr-btn hr-btn-sm hr-btn-secondary"
                    @click="openEdit(r)"
                  >
                    Edit
                  </button>
                </td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="10" class="hr-text-center hr-text-muted hr-py-4">No records.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <ModalWrapper ref="formModal" :title="form.id ? 'Edit incoming' : 'Log incoming correspondence'" size="large">
      <template #body>
        <form class="hr-form-grid" @submit.prevent>
          <div>
            <label class="hr-form-label">Received date <span class="hr-text-danger">*</span></label>
            <input v-model="form.received_date" type="date" class="hr-input" required />
          </div>
          <div>
            <label class="hr-form-label">Correspondence date <span class="hr-text-danger">*</span></label>
            <input v-model="form.correspondence_date" type="date" class="hr-input" required />
          </div>
          <div class="span-full">
            <label class="hr-form-label">Sender <span class="hr-text-danger">*</span></label>
            <input v-model="form.sender" type="text" class="hr-input" required />
          </div>
          <div class="span-full">
            <label class="hr-form-label">Subject <span class="hr-text-danger">*</span></label>
            <input v-model="form.subject" type="text" class="hr-input" required />
          </div>
          <div>
            <label class="hr-form-label">Reference number <span class="hr-text-danger">*</span></label>
            <input v-model="form.ref_number" type="text" class="hr-input" required />
          </div>
          <div>
            <label class="hr-form-label">Handler <span class="hr-text-danger">*</span></label>
            <input v-model="form.handler" type="text" class="hr-input" required placeholder="Officer responsible" />
          </div>
          <div>
            <label class="hr-form-label">Status</label>
            <select v-model="form.status" class="hr-select">
              <option value="Received">Received</option>
              <option value="Under Review">Under Review</option>
              <option value="Responded">Responded</option>
              <option value="Filed">Filed</option>
            </select>
          </div>
          <div class="span-full">
            <label class="hr-form-label">Notes</label>
            <textarea v-model="form.notes" class="hr-textarea" rows="2"></textarea>
          </div>
          <div v-if="!form.id" class="span-full">
            <label class="hr-form-label">Attachment (optional)</label>
            <input ref="fileInput" type="file" class="hr-input" accept=".pdf,.zip,.doc,.docx,.png,.jpg,.jpeg" />
            <small class="hr-text-muted">Max 15 MB. PDF/ZIP preferred for General Documents archive.</small>
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.formModal.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-success" :disabled="saving" @click="saveForm">{{ saving ? "Saving…" : "Save" }}</button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "IncomingAttorneyDocuments",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      saving: false,
      queue: "active",
      rows: [],
      installHint: "",
      filters: {
        status: "",
        date_from: "",
        date_to: "",
        sender: "",
        ref_number: "",
        keyword: ""
      },
      form: {
        id: null,
        received_date: "",
        correspondence_date: "",
        sender: "",
        subject: "",
        ref_number: "",
        handler: "",
        status: "Received",
        notes: ""
      }
    };
  },
  mounted() {
    this.loadRows();
  },
  methods: {
    setQueue(q) {
      this.queue = q;
      this.loadRows();
    },
    resetFilters() {
      this.filters = {
        status: "",
        date_from: "",
        date_to: "",
        sender: "",
        ref_number: "",
        keyword: ""
      };
      this.loadRows();
    },
    async loadRows() {
      this.loading = true;
      this.installHint = "";
      try {
        const fd = new FormData();
        fd.append("function", "listIncomingAttorneyDocuments");
        fd.append("queue", this.queue);
        if (this.filters.status) fd.append("status", this.filters.status);
        if (this.filters.date_from) fd.append("date_from", this.filters.date_from);
        if (this.filters.date_to) fd.append("date_to", this.filters.date_to);
        if (this.filters.sender) fd.append("sender", this.filters.sender);
        if (this.filters.ref_number) fd.append("ref_number", this.filters.ref_number);
        if (this.filters.keyword) fd.append("keyword", this.filters.keyword);
        const res = await execute(fd, authUrl);
        const msg = res.data?.message || "";
        if (msg && msg.indexOf("incoming_attorney_documents_module") !== -1) {
          this.installHint = "Database module not installed. Run authentication/sql/incoming_attorney_documents_module.sql";
        }
        this.rows = res.data?.success && Array.isArray(res.data.data?.rows) ? res.data.data.rows : [];
      } catch (e) {
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      const t = new Date();
      const y = t.getFullYear();
      const m = String(t.getMonth() + 1).padStart(2, "0");
      const d = String(t.getDate()).padStart(2, "0");
      const today = `${y}-${m}-${d}`;
      this.form = {
        id: null,
        received_date: today,
        correspondence_date: today,
        sender: "",
        subject: "",
        ref_number: "",
        handler: "",
        status: "Received",
        notes: ""
      };
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      this.$refs.formModal.open();
    },
    openEdit(r) {
      this.form = {
        id: r.id,
        received_date: (r.received_date || "").slice(0, 10),
        correspondence_date: (r.correspondence_date || "").slice(0, 10),
        sender: r.sender || "",
        subject: r.subject || "",
        ref_number: r.ref_number || "",
        handler: r.handler || "",
        status: r.status || "Received",
        notes: r.notes || ""
      };
      this.$refs.formModal.open();
    },
    async saveForm() {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "saveIncomingAttorneyDocument");
        if (this.form.id) fd.append("id", String(this.form.id));
        fd.append("received_date", this.form.received_date);
        fd.append("correspondence_date", this.form.correspondence_date);
        fd.append("sender", this.form.sender);
        fd.append("subject", this.form.subject);
        fd.append("ref_number", this.form.ref_number);
        fd.append("handler", this.form.handler);
        fd.append("status", this.form.status);
        fd.append("notes", this.form.notes || "");
        const f = this.$refs.fileInput?.files?.[0];
        if (f && !this.form.id) fd.append("attachment", f);
        const res = await execute(fd, authUrl);
        if (!res.data?.success) {
          alert(res.data?.message || "Save failed");
          return;
        }
        this.$refs.formModal.close();
        await this.loadRows();
      } catch (e) {
        alert(e?.message || "Error");
      } finally {
        this.saving = false;
      }
    },
    async fetchAttachmentBlob(id, viewMode) {
      const fd = new FormData();
      fd.append("function", viewMode ? "viewIncomingAttorneyDocument" : "downloadIncomingAttorneyDocument");
      fd.append("id", String(id));
      const res = await executeDownload(fd, authUrl);
      const ct = (res.headers && res.headers["content-type"]) || "";
      if (ct.indexOf("application/json") !== -1) {
        const rawText = res.data instanceof Blob ? await res.data.text() : String(res.data);
        let msg = viewMode ? "Could not view document." : "Download failed.";
        try {
          const j = JSON.parse(rawText);
          if (j.message) msg = j.message;
        } catch (_) {}
        throw new Error(msg);
      }
      return res.data instanceof Blob ? res.data : new Blob([res.data], { type: ct || "application/octet-stream" });
    },
    async viewFile(id) {
      try {
        const blob = await this.fetchAttachmentBlob(id, true);
        const url = window.URL.createObjectURL(blob);
        const opened = window.open(url, "_blank");
        if (!opened) {
          alert("Pop-up blocked. Allow pop-ups to view the document, or use Download.");
          window.URL.revokeObjectURL(url);
          return;
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 120000);
      } catch (e) {
        alert(e?.message || "Could not view document");
      }
    },
    async downloadFile(id) {
      try {
        const blob = await this.fetchAttachmentBlob(id, false);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "incoming-attachment";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        alert(e?.message || "Download failed");
      }
    }
  }
};
</script>

<style scoped>
.hr-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.hr-form-grid .span-full {
  grid-column: 1 / -1;
}
.iad-file-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
}

.iad-toolbar .tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.tab {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  color: #475569;
}
.tab.on {
  background: #0f172a;
  color: #fff;
  border-color: #0f172a;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}
.filters label span {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}
.filters .grow {
  flex: 1 1 160px;
  min-width: 140px;
}
</style>
